import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import { adminSettingsApi } from '../../../services/api'
import { Save, RefreshCw, AlertTriangle, CheckCircle, Briefcase, FileText, Calculator, Shield, Bell, BarChart3, CheckSquare } from 'lucide-react'

interface EnterpriseSettings {
  rfqDefaults: {
    defaultDeadlineDays: number
    defaultResponseWindowDays: number
    defaultVisibility: string
    autoPublish: boolean
    requireAttachments: boolean
    maxAttachments: number
    allowAnonymousRfqs: boolean
  }
  quoteWorkflows: {
    requirePoUpload: boolean
    poApprovalRequired: boolean
    signatureProvider: string
    quoteExpirationDays: number
    autoAcceptThreshold: number | null
    revisionLimit: number
    digitalSignatureRequired: boolean
  }
  roiSimulator: {
    defaultDegradationRate: number
    defaultElectricityCost: number
    defaultMaintenanceCost: number
    defaultInsuranceCost: number
    calculationMethod: string
    taxIncentivesEnabled: boolean
    defaultTaxIncentiveRate: number
  }
  permissions: {
    vendorCanEditQuotes: boolean
    vendorCanWithdrawQuotes: boolean
    buyerApprovalSteps: number
    maxQuoteValue: number | null
    requireAdminReviewAbove: number | null
    allowDirectNegotiation: boolean
  }
  notifications: {
    notifyOnNewRfq: boolean
    notifyOnQuoteSubmitted: boolean
    notifyOnQuoteAccepted: boolean
    notifyOnQuoteExpired: boolean
    emailDigestFrequency: string
    pushNotificationsEnabled: boolean
    smsNotificationsEnabled: boolean
    emailTemplates: {
      rfqCreated: string
      quoteSubmitted: string
      quoteAccepted: string
      quoteRejected: string
    }
  }
  reporting: {
    enableCsvExport: boolean
    enablePdfReports: boolean
    dataRetentionDays: number
    autoArchiveCompletedRfqs: boolean
    archiveAfterDays: number
    analyticsEnabled: boolean
    trackVendorPerformance: boolean
  }
  security: {
    requireAdminReviewForHighValue: boolean
    highValueThreshold: number
    encryptDocuments: boolean
    requireMfa: boolean
    auditLogRetentionDays: number
    ipWhitelistEnabled: boolean
    allowedIpRanges: string[]
  }
  compliance: {
    requireTermsAcceptance: boolean
    requireNda: boolean
    documentRetentionYears: number
    gdprCompliant: boolean
    hipaaCompliant: boolean
    soc2Compliant: boolean
  }
  lastModified: string
  lastModifiedBy: string
}

const EnterpriseSettingsPanel: React.FC = () => {
  const [settings, setSettings] = useState<EnterpriseSettings>({
    rfqDefaults: {
      defaultDeadlineDays: 14,
      defaultResponseWindowDays: 7,
      defaultVisibility: 'certified-vendors',
      autoPublish: false,
      requireAttachments: true,
      maxAttachments: 10,
      allowAnonymousRfqs: false
    },
    quoteWorkflows: {
      requirePoUpload: true,
      poApprovalRequired: true,
      signatureProvider: 'docusign',
      quoteExpirationDays: 30,
      autoAcceptThreshold: null,
      revisionLimit: 3,
      digitalSignatureRequired: true
    },
    roiSimulator: {
      defaultDegradationRate: 0.8,
      defaultElectricityCost: 0.13,
      defaultMaintenanceCost: 150,
      defaultInsuranceCost: 200,
      calculationMethod: 'npv',
      taxIncentivesEnabled: true,
      defaultTaxIncentiveRate: 26
    },
    permissions: {
      vendorCanEditQuotes: true,
      vendorCanWithdrawQuotes: true,
      buyerApprovalSteps: 2,
      maxQuoteValue: null,
      requireAdminReviewAbove: 100000,
      allowDirectNegotiation: true
    },
    notifications: {
      notifyOnNewRfq: true,
      notifyOnQuoteSubmitted: true,
      notifyOnQuoteAccepted: true,
      notifyOnQuoteExpired: false,
      emailDigestFrequency: 'daily',
      pushNotificationsEnabled: true,
      smsNotificationsEnabled: false,
      emailTemplates: {
        rfqCreated: 'New RFQ "{{rfqTitle}}" has been created. Deadline: {{deadline}}',
        quoteSubmitted: 'Vendor {{vendorName}} submitted a quote for "{{rfqTitle}}"',
        quoteAccepted: 'Congratulations! Your quote for "{{rfqTitle}}" has been accepted.',
        quoteRejected: 'Your quote for "{{rfqTitle}}" was not selected. Reason: {{reason}}'
      }
    },
    reporting: {
      enableCsvExport: true,
      enablePdfReports: true,
      dataRetentionDays: 2555,
      autoArchiveCompletedRfqs: true,
      archiveAfterDays: 90,
      analyticsEnabled: true,
      trackVendorPerformance: true
    },
    security: {
      requireAdminReviewForHighValue: true,
      highValueThreshold: 50000,
      encryptDocuments: true,
      requireMfa: false,
      auditLogRetentionDays: 2555,
      ipWhitelistEnabled: false,
      allowedIpRanges: []
    },
    compliance: {
      requireTermsAcceptance: true,
      requireNda: false,
      documentRetentionYears: 7,
      gdprCompliant: true,
      hipaaCompliant: false,
      soc2Compliant: false
    },
    lastModified: new Date().toISOString(),
    lastModifiedBy: 'admin@evoltbay.com'
  })

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [authError, setAuthError] = useState(false)

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    try {
      setLoading(true)
      const response = await adminSettingsApi.getSettings('enterprise')
      if (response.success) {
        setSettings(response.data)
      } else {
        console.warn('API returned success=false:', response)
        // Keep default settings on API error
      }
    } catch (error: any) {
      console.error('Failed to load enterprise settings:', error)

      // Handle authentication errors
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.warn('Authentication error - user may not be logged in as admin')
        setAuthError(true)
      }

      // Keep default settings on error so UI doesn't crash
    } finally {
      setLoading(false)
    }
  }

  const handleSettingChange = (path: string, value: any) => {
    setSettings(prev => {
      const newSettings = { ...prev }
      const keys = path.split('.')
      let current: any = newSettings

      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]]
      }
      current[keys[keys.length - 1]] = value

      return newSettings
    })
    setHasChanges(true)
  }

  const handleArrayChange = (path: string, value: string[]) => {
    setSettings(prev => {
      const newSettings = { ...prev }
      const keys = path.split('.')
      let current: any = newSettings

      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]]
      }
      current[keys[keys.length - 1]] = value

      return newSettings
    })
    setHasChanges(true)
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      const response = await adminSettingsApi.updateSettings('enterprise', settings)
      if (response.success) {
        setSettings(response.data)
        setHasChanges(false)
        toast.success('Enterprise settings saved successfully!')
      }
    } catch (error) {
      console.error('Failed to save enterprise settings:', error)
      toast.error('Failed to save enterprise settings')
    } finally {
      setSaving(false)
    }
  }

  const handleReset = async () => {
    try {
      setSaving(true)
      const response = await adminSettingsApi.resetSettings('enterprise')
      if (response.success) {
        setSettings(response.data)
        setHasChanges(false)
        toast.success('Enterprise settings reset to defaults!')
      }
    } catch (error) {
      console.error('Failed to reset enterprise settings:', error)
      toast.error('Failed to reset enterprise settings')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="py-12 text-center">
        <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
        <p className="text-gray-600">Loading enterprise settings...</p>
      </div>
    )
  }

  if (authError) {
    return (
      <div className="py-12 text-center">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 max-w-md mx-auto">
          <div className="flex items-center justify-center mb-4">
            <AlertTriangle className="h-12 w-12 text-yellow-600" />
          </div>
          <h3 className="text-lg font-medium text-yellow-800 mb-2">
            Authentication Required
          </h3>
          <p className="text-yellow-700 mb-4">
            You need to be logged in as an admin to access these settings.
          </p>
          <p className="text-sm text-yellow-600">
            Please log in with admin credentials and try again.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center space-x-2 sm:space-x-3">
          <Briefcase className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600 flex-shrink-0" />
          <div>
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">Enterprise Quotation System</h2>
            <p className="text-gray-600 text-sm sm:text-base">Configure RFQ workflows, quote management, ROI simulator, and compliance</p>
          </div>
        </div>

        {/* Save/Reset Actions */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
          <button
            onClick={handleReset}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!hasChanges}
          >
            Reset
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="flex items-center justify-center space-x-2 px-4 sm:px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
          >
            {saving ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span className="text-sm sm:text-base">{saving ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </div>
      </div>

      {/* Last Modified Info */}
      <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
        <CheckCircle className="h-4 w-4 text-green-600" />
        <span className="text-sm text-gray-600">
          Last modified: {new Date(settings.lastModified).toLocaleString()} by {settings.lastModifiedBy}
        </span>
      </div>

      {/* Settings Sections */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 sm:gap-8">
        {/* RFQ Defaults */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <FileText className="h-5 w-5 mr-2 text-blue-600" />
            RFQ Defaults
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Default Deadline (Days)</label>
              <input
                type="number"
                min="1"
                max="365"
                value={settings.rfqDefaults.defaultDeadlineDays}
                onChange={(e) => handleSettingChange('rfqDefaults.defaultDeadlineDays', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Response Window (Days)</label>
              <input
                type="number"
                min="1"
                max="90"
                value={settings.rfqDefaults.defaultResponseWindowDays}
                onChange={(e) => handleSettingChange('rfqDefaults.defaultResponseWindowDays', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Default Visibility</label>
              <select
                value={settings.rfqDefaults.defaultVisibility}
                onChange={(e) => handleSettingChange('rfqDefaults.defaultVisibility', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all-vendors">All Vendors</option>
                <option value="certified-vendors">Certified Vendors Only</option>
                <option value="invited-only">Invited Only</option>
                <option value="private">Private</option>
              </select>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Auto-Publish</label>
                <p className="text-xs text-gray-500">Automatically publish RFQs upon creation</p>
              </div>
              <input
                type="checkbox"
                checked={settings.rfqDefaults.autoPublish}
                onChange={(e) => handleSettingChange('rfqDefaults.autoPublish', e.target.checked)}
                className="h-4 w-4 text-blue-600 rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Require Attachments</label>
                <p className="text-xs text-gray-500">RFQs must include supporting documents</p>
              </div>
              <input
                type="checkbox"
                checked={settings.rfqDefaults.requireAttachments}
                onChange={(e) => handleSettingChange('rfqDefaults.requireAttachments', e.target.checked)}
                className="h-4 w-4 text-blue-600 rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Max Attachments</label>
              <input
                type="number"
                min="1"
                max="50"
                value={settings.rfqDefaults.maxAttachments}
                onChange={(e) => handleSettingChange('rfqDefaults.maxAttachments', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Allow Anonymous RFQs</label>
                <p className="text-xs text-gray-500">Buyers can create anonymous RFQs</p>
              </div>
              <input
                type="checkbox"
                checked={settings.rfqDefaults.allowAnonymousRfqs}
                onChange={(e) => handleSettingChange('rfqDefaults.allowAnonymousRfqs', e.target.checked)}
                className="h-4 w-4 text-blue-600 rounded"
              />
            </div>
          </div>
        </div>

        {/* Quote Workflows */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <CheckSquare className="h-5 w-5 mr-2 text-green-600" />
            Quote Workflows
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Require PO Upload</label>
                <p className="text-xs text-gray-500">Purchase order required for acceptance</p>
              </div>
              <input
                type="checkbox"
                checked={settings.quoteWorkflows.requirePoUpload}
                onChange={(e) => handleSettingChange('quoteWorkflows.requirePoUpload', e.target.checked)}
                className="h-4 w-4 text-green-600 rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">PO Approval Required</label>
                <p className="text-xs text-gray-500">Admin must approve PO before processing</p>
              </div>
              <input
                type="checkbox"
                checked={settings.quoteWorkflows.poApprovalRequired}
                onChange={(e) => handleSettingChange('quoteWorkflows.poApprovalRequired', e.target.checked)}
                className="h-4 w-4 text-green-600 rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Signature Provider</label>
              <select
                value={settings.quoteWorkflows.signatureProvider}
                onChange={(e) => handleSettingChange('quoteWorkflows.signatureProvider', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              >
                <option value="none">None</option>
                <option value="docusign">DocuSign</option>
                <option value="hellosign">HelloSign</option>
                <option value="adobe-sign">Adobe Sign</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Quote Expiration (Days)</label>
              <input
                type="number"
                min="1"
                max="365"
                value={settings.quoteWorkflows.quoteExpirationDays}
                onChange={(e) => handleSettingChange('quoteWorkflows.quoteExpirationDays', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Auto-Accept Threshold ($)</label>
              <input
                type="number"
                min="0"
                placeholder="Leave empty to disable"
                value={settings.quoteWorkflows.autoAcceptThreshold || ''}
                onChange={(e) => handleSettingChange('quoteWorkflows.autoAcceptThreshold', e.target.value ? parseFloat(e.target.value) : null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
              <p className="text-xs text-gray-500 mt-1">Automatically accept quotes below this value</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Revision Limit</label>
              <input
                type="number"
                min="1"
                max="10"
                value={settings.quoteWorkflows.revisionLimit}
                onChange={(e) => handleSettingChange('quoteWorkflows.revisionLimit', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Digital Signature Required</label>
                <p className="text-xs text-gray-500">Require e-signature for quote acceptance</p>
              </div>
              <input
                type="checkbox"
                checked={settings.quoteWorkflows.digitalSignatureRequired}
                onChange={(e) => handleSettingChange('quoteWorkflows.digitalSignatureRequired', e.target.checked)}
                className="h-4 w-4 text-green-600 rounded"
              />
            </div>
          </div>
        </div>

        {/* ROI Simulator */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Calculator className="h-5 w-5 mr-2 text-orange-600" />
            ROI Simulator
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Default Degradation Rate (%/year)</label>
              <input
                type="number"
                min="0"
                max="10"
                step="0.1"
                value={settings.roiSimulator.defaultDegradationRate}
                onChange={(e) => handleSettingChange('roiSimulator.defaultDegradationRate', parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Default Electricity Cost ($/kWh)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={settings.roiSimulator.defaultElectricityCost}
                onChange={(e) => handleSettingChange('roiSimulator.defaultElectricityCost', parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Default Maintenance Cost ($/year)</label>
              <input
                type="number"
                min="0"
                value={settings.roiSimulator.defaultMaintenanceCost}
                onChange={(e) => handleSettingChange('roiSimulator.defaultMaintenanceCost', parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Default Insurance Cost ($/year)</label>
              <input
                type="number"
                min="0"
                value={settings.roiSimulator.defaultInsuranceCost}
                onChange={(e) => handleSettingChange('roiSimulator.defaultInsuranceCost', parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Calculation Method</label>
              <select
                value={settings.roiSimulator.calculationMethod}
                onChange={(e) => handleSettingChange('roiSimulator.calculationMethod', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
              >
                <option value="simple">Simple ROI</option>
                <option value="npv">Net Present Value (NPV)</option>
                <option value="irr">Internal Rate of Return (IRR)</option>
                <option value="payback">Payback Period</option>
              </select>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Tax Incentives Enabled</label>
                <p className="text-xs text-gray-500">Include tax credits in calculations</p>
              </div>
              <input
                type="checkbox"
                checked={settings.roiSimulator.taxIncentivesEnabled}
                onChange={(e) => handleSettingChange('roiSimulator.taxIncentivesEnabled', e.target.checked)}
                className="h-4 w-4 text-orange-600 rounded"
              />
            </div>

            {settings.roiSimulator.taxIncentivesEnabled && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Default Tax Incentive Rate (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={settings.roiSimulator.defaultTaxIncentiveRate}
                  onChange={(e) => handleSettingChange('roiSimulator.defaultTaxIncentiveRate', parseFloat(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
            )}
          </div>
        </div>

        {/* Permissions */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Shield className="h-5 w-5 mr-2 text-red-600" />
            Permissions
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Vendor Can Edit Quotes</label>
                <p className="text-xs text-gray-500">Allow vendors to edit submitted quotes</p>
              </div>
              <input
                type="checkbox"
                checked={settings.permissions.vendorCanEditQuotes}
                onChange={(e) => handleSettingChange('permissions.vendorCanEditQuotes', e.target.checked)}
                className="h-4 w-4 text-red-600 rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Vendor Can Withdraw Quotes</label>
                <p className="text-xs text-gray-500">Allow vendors to withdraw quotes</p>
              </div>
              <input
                type="checkbox"
                checked={settings.permissions.vendorCanWithdrawQuotes}
                onChange={(e) => handleSettingChange('permissions.vendorCanWithdrawQuotes', e.target.checked)}
                className="h-4 w-4 text-red-600 rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Buyer Approval Steps</label>
              <input
                type="number"
                min="0"
                max="5"
                value={settings.permissions.buyerApprovalSteps}
                onChange={(e) => handleSettingChange('permissions.buyerApprovalSteps', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Max Quote Value ($)</label>
              <input
                type="number"
                min="0"
                placeholder="Leave empty for no limit"
                value={settings.permissions.maxQuoteValue || ''}
                onChange={(e) => handleSettingChange('permissions.maxQuoteValue', e.target.value ? parseFloat(e.target.value) : null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Require Admin Review Above ($)</label>
              <input
                type="number"
                min="0"
                placeholder="Leave empty to disable"
                value={settings.permissions.requireAdminReviewAbove || ''}
                onChange={(e) => handleSettingChange('permissions.requireAdminReviewAbove', e.target.value ? parseFloat(e.target.value) : null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Allow Direct Negotiation</label>
                <p className="text-xs text-gray-500">Buyers and vendors can negotiate directly</p>
              </div>
              <input
                type="checkbox"
                checked={settings.permissions.allowDirectNegotiation}
                onChange={(e) => handleSettingChange('permissions.allowDirectNegotiation', e.target.checked)}
                className="h-4 w-4 text-red-600 rounded"
              />
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Bell className="h-5 w-5 mr-2 text-yellow-600" />
            Notifications
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Notify on New RFQ</label>
                <p className="text-xs text-gray-500">Alert vendors when new RFQs are posted</p>
              </div>
              <input
                type="checkbox"
                checked={settings.notifications.notifyOnNewRfq}
                onChange={(e) => handleSettingChange('notifications.notifyOnNewRfq', e.target.checked)}
                className="h-4 w-4 text-yellow-600 rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Notify on Quote Submitted</label>
                <p className="text-xs text-gray-500">Alert buyers when quotes are submitted</p>
              </div>
              <input
                type="checkbox"
                checked={settings.notifications.notifyOnQuoteSubmitted}
                onChange={(e) => handleSettingChange('notifications.notifyOnQuoteSubmitted', e.target.checked)}
                className="h-4 w-4 text-yellow-600 rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Notify on Quote Accepted</label>
                <p className="text-xs text-gray-500">Alert vendors when quotes are accepted</p>
              </div>
              <input
                type="checkbox"
                checked={settings.notifications.notifyOnQuoteAccepted}
                onChange={(e) => handleSettingChange('notifications.notifyOnQuoteAccepted', e.target.checked)}
                className="h-4 w-4 text-yellow-600 rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Notify on Quote Expired</label>
                <p className="text-xs text-gray-500">Alert vendors when quotes expire</p>
              </div>
              <input
                type="checkbox"
                checked={settings.notifications.notifyOnQuoteExpired}
                onChange={(e) => handleSettingChange('notifications.notifyOnQuoteExpired', e.target.checked)}
                className="h-4 w-4 text-yellow-600 rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Digest Frequency</label>
              <select
                value={settings.notifications.emailDigestFrequency}
                onChange={(e) => handleSettingChange('notifications.emailDigestFrequency', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
              >
                <option value="realtime">Real-time</option>
                <option value="hourly">Hourly</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="never">Never</option>
              </select>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Push Notifications</label>
                <p className="text-xs text-gray-500">Enable browser push notifications</p>
              </div>
              <input
                type="checkbox"
                checked={settings.notifications.pushNotificationsEnabled}
                onChange={(e) => handleSettingChange('notifications.pushNotificationsEnabled', e.target.checked)}
                className="h-4 w-4 text-yellow-600 rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">SMS Notifications</label>
                <p className="text-xs text-gray-500">Enable SMS alerts for critical events</p>
              </div>
              <input
                type="checkbox"
                checked={settings.notifications.smsNotificationsEnabled}
                onChange={(e) => handleSettingChange('notifications.smsNotificationsEnabled', e.target.checked)}
                className="h-4 w-4 text-yellow-600 rounded"
              />
            </div>

            <div className="border-t pt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Email Templates</h4>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">RFQ Created</label>
                  <textarea
                    rows={2}
                    value={settings.notifications.emailTemplates.rfqCreated}
                    onChange={(e) => handleSettingChange('notifications.emailTemplates.rfqCreated', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs text-gray-600 mb-1">Quote Submitted</label>
                  <textarea
                    rows={2}
                    value={settings.notifications.emailTemplates.quoteSubmitted}
                    onChange={(e) => handleSettingChange('notifications.emailTemplates.quoteSubmitted', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs text-gray-600 mb-1">Quote Accepted</label>
                  <textarea
                    rows={2}
                    value={settings.notifications.emailTemplates.quoteAccepted}
                    onChange={(e) => handleSettingChange('notifications.emailTemplates.quoteAccepted', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs text-gray-600 mb-1">Quote Rejected</label>
                  <textarea
                    rows={2}
                    value={settings.notifications.emailTemplates.quoteRejected}
                    onChange={(e) => handleSettingChange('notifications.emailTemplates.quoteRejected', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 text-sm"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Reporting */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <BarChart3 className="h-5 w-5 mr-2 text-indigo-600" />
            Reporting & Analytics
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Enable CSV Export</label>
                <p className="text-xs text-gray-500">Allow users to export data as CSV</p>
              </div>
              <input
                type="checkbox"
                checked={settings.reporting.enableCsvExport}
                onChange={(e) => handleSettingChange('reporting.enableCsvExport', e.target.checked)}
                className="h-4 w-4 text-indigo-600 rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Enable PDF Reports</label>
                <p className="text-xs text-gray-500">Generate PDF reports for RFQs and quotes</p>
              </div>
              <input
                type="checkbox"
                checked={settings.reporting.enablePdfReports}
                onChange={(e) => handleSettingChange('reporting.enablePdfReports', e.target.checked)}
                className="h-4 w-4 text-indigo-600 rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Data Retention (Days)</label>
              <input
                type="number"
                min="1"
                value={settings.reporting.dataRetentionDays}
                onChange={(e) => handleSettingChange('reporting.dataRetentionDays', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Auto-Archive Completed RFQs</label>
                <p className="text-xs text-gray-500">Automatically archive completed RFQs</p>
              </div>
              <input
                type="checkbox"
                checked={settings.reporting.autoArchiveCompletedRfqs}
                onChange={(e) => handleSettingChange('reporting.autoArchiveCompletedRfqs', e.target.checked)}
                className="h-4 w-4 text-indigo-600 rounded"
              />
            </div>

            {settings.reporting.autoArchiveCompletedRfqs && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Archive After (Days)</label>
                <input
                  type="number"
                  min="1"
                  value={settings.reporting.archiveAfterDays}
                  onChange={(e) => handleSettingChange('reporting.archiveAfterDays', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            )}

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Analytics Enabled</label>
                <p className="text-xs text-gray-500">Track RFQ and quote performance metrics</p>
              </div>
              <input
                type="checkbox"
                checked={settings.reporting.analyticsEnabled}
                onChange={(e) => handleSettingChange('reporting.analyticsEnabled', e.target.checked)}
                className="h-4 w-4 text-indigo-600 rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Track Vendor Performance</label>
                <p className="text-xs text-gray-500">Monitor vendor response times and quality</p>
              </div>
              <input
                type="checkbox"
                checked={settings.reporting.trackVendorPerformance}
                onChange={(e) => handleSettingChange('reporting.trackVendorPerformance', e.target.checked)}
                className="h-4 w-4 text-indigo-600 rounded"
              />
            </div>
          </div>
        </div>

        {/* Security */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Shield className="h-5 w-5 mr-2 text-red-600" />
            Security & Audit
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">High-Value Review Required</label>
                <p className="text-xs text-gray-500">Admin review for high-value quotes</p>
              </div>
              <input
                type="checkbox"
                checked={settings.security.requireAdminReviewForHighValue}
                onChange={(e) => handleSettingChange('security.requireAdminReviewForHighValue', e.target.checked)}
                className="h-4 w-4 text-red-600 rounded"
              />
            </div>

            {settings.security.requireAdminReviewForHighValue && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">High-Value Threshold ($)</label>
                <input
                  type="number"
                  min="0"
                  value={settings.security.highValueThreshold}
                  onChange={(e) => handleSettingChange('security.highValueThreshold', parseFloat(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                />
              </div>
            )}

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Encrypt Documents</label>
                <p className="text-xs text-gray-500">Encrypt all uploaded documents at rest</p>
              </div>
              <input
                type="checkbox"
                checked={settings.security.encryptDocuments}
                onChange={(e) => handleSettingChange('security.encryptDocuments', e.target.checked)}
                className="h-4 w-4 text-red-600 rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Require MFA</label>
                <p className="text-xs text-gray-500">Multi-factor auth for enterprise users</p>
              </div>
              <input
                type="checkbox"
                checked={settings.security.requireMfa}
                onChange={(e) => handleSettingChange('security.requireMfa', e.target.checked)}
                className="h-4 w-4 text-red-600 rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Audit Log Retention (Days)</label>
              <input
                type="number"
                min="1"
                value={settings.security.auditLogRetentionDays}
                onChange={(e) => handleSettingChange('security.auditLogRetentionDays', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">IP Whitelist Enabled</label>
                <p className="text-xs text-gray-500">Restrict access to specific IP ranges</p>
              </div>
              <input
                type="checkbox"
                checked={settings.security.ipWhitelistEnabled}
                onChange={(e) => handleSettingChange('security.ipWhitelistEnabled', e.target.checked)}
                className="h-4 w-4 text-red-600 rounded"
              />
            </div>

            {settings.security.ipWhitelistEnabled && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Allowed IP Ranges</label>
                <textarea
                  rows={3}
                  placeholder="Enter IP ranges, one per line (e.g., 192.168.1.0/24)"
                  value={settings.security.allowedIpRanges.join('\n')}
                  onChange={(e) => handleArrayChange('security.allowedIpRanges', e.target.value.split('\n').filter(ip => ip.trim()))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 font-mono text-sm"
                />
              </div>
            )}
          </div>
        </div>

        {/* Compliance */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <CheckCircle className="h-5 w-5 mr-2 text-green-600" />
            Compliance & Legal
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Require Terms Acceptance</label>
                <p className="text-xs text-gray-500">Users must accept T&C before quoting</p>
              </div>
              <input
                type="checkbox"
                checked={settings.compliance.requireTermsAcceptance}
                onChange={(e) => handleSettingChange('compliance.requireTermsAcceptance', e.target.checked)}
                className="h-4 w-4 text-green-600 rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Require NDA</label>
                <p className="text-xs text-gray-500">Require NDA signature for sensitive RFQs</p>
              </div>
              <input
                type="checkbox"
                checked={settings.compliance.requireNda}
                onChange={(e) => handleSettingChange('compliance.requireNda', e.target.checked)}
                className="h-4 w-4 text-green-600 rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Document Retention (Years)</label>
              <input
                type="number"
                min="1"
                max="50"
                value={settings.compliance.documentRetentionYears}
                onChange={(e) => handleSettingChange('compliance.documentRetentionYears', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>

            <div className="border-t pt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Compliance Standards</h4>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">GDPR Compliant</label>
                    <p className="text-xs text-gray-500">EU data protection compliance</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.compliance.gdprCompliant}
                    onChange={(e) => handleSettingChange('compliance.gdprCompliant', e.target.checked)}
                    className="h-4 w-4 text-green-600 rounded"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">HIPAA Compliant</label>
                    <p className="text-xs text-gray-500">Healthcare data protection</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.compliance.hipaaCompliant}
                    onChange={(e) => handleSettingChange('compliance.hipaaCompliant', e.target.checked)}
                    className="h-4 w-4 text-green-600 rounded"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">SOC 2 Compliant</label>
                    <p className="text-xs text-gray-500">Security and availability standards</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.compliance.soc2Compliant}
                    onChange={(e) => handleSettingChange('compliance.soc2Compliant', e.target.checked)}
                    className="h-4 w-4 text-green-600 rounded"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Warning Banner */}
      {hasChanges && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-yellow-600" />
            <div>
              <p className="text-yellow-800 font-medium">Unsaved Changes</p>
              <p className="text-yellow-700 text-sm">You have unsaved changes. Click "Save Changes" to apply them.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default EnterpriseSettingsPanel
